/**
 * viewerIdentity.js — who a requester or grantee is, one label everywhere
 * (an owner's prompt, grant lists, token history): @handle for a viewer
 * signed in to acequia; for a guest, its browser's device name and the start
 * of its user id, marked guest. The app it uses goes beside it.
 *
 * Grant records carry the parts (granteeHandle, granteeDeviceName,
 * granteeUserId, granteeApp, deviceId); records from before 2026-10-01 have
 * only a name like "CameraMap (Full)", read as the app. Moved from GeoCamera
 * (src/modules/viewer-identity.js) for the grant manager and its components
 * (unified-token-system, Phases 4–5).
 */

/** The parts of an identity, from an access request or a grant record. */
export function identityOf(src = {}) {
  const legacyApp = src.name ? String(src.name).replace(/\s*\([^)]*\)\s*$/, '') : null
  return {
    handle: src.handle ?? src.granteeHandle ?? null,
    deviceName: src.deviceName ?? src.granteeDeviceName ?? null,
    userId: src.userId ?? src.granteeUserId ?? null,
    deviceId: src.deviceId ?? null,
    app: src.app ?? src.granteeApp ?? src.viewerName ?? src.displayName ?? legacyApp,
  }
}

/** A short id to tell guests apart: the start of the user id (else the device id). */
export function shortId(who) {
  return String(who.userId || who.deviceId || '').slice(0, 4)
}

export function isGuest(who) {
  return !who.handle
}

/** "@handle", or "swift-otter · a1b2" for a guest. */
export function whoText(who) {
  if (who.handle) return `@${who.handle}`
  const sid = shortId(who)
  return `${who.deviceName || 'unnamed browser'}${sid ? ` · ${sid}` : ''}`
}

/** One line: "@handle — CameraMap", "swift-otter · a1b2 (guest) — CameraMap". */
export function identityText(who) {
  return `${whoText(who)}${isGuest(who) ? ' (guest)' : ''}${who.app ? ` — ${who.app}` : ''}`
}

/** The stored name of a grant: the identity and what it grants. */
export function grantRecordName(who, label) {
  return `${identityText(who)}${label ? ` (${label})` : ''}`
}

/** The label as HTML: the who bold, a Guest badge, the app muted. */
export function identityHtml(who) {
  const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;')
  const guest = isGuest(who)
  const title = [
    who.handle ? `Signed in as @${who.handle}` : 'Not signed in to acequia',
    who.deviceName ? `device ${who.deviceName}` : null,
    who.userId ? `user ${who.userId}` : null,
  ].filter(Boolean).join(' · ')
  return `<span class="viewer-id" title="${esc(title)}">`
    + `<strong>${esc(whoText(who))}</strong>`
    + (guest ? ' <span class="viewer-guest" title="Not signed in to acequia">guest</span>' : '')
    + (who.app ? ` <span class="viewer-app">${esc(who.app)}</span>` : '')
    + '</span>'
}

/** "just now", "5m ago", "3h ago", "2d ago" — for last use and requests. */
export function agoText(iso) {
  if (!iso) return 'never'
  const ms = Date.now() - Date.parse(iso)
  if (!(ms >= 0)) return 'never'
  const m = Math.floor(ms / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}
